import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangleIcon,
  ArrowRightIcon,
  CheckCircle2Icon,
  ClipboardListIcon,
  ExternalLinkIcon,
  GraduationCapIcon,
  HeartHandshakeIcon,
  LogInIcon,
  MessageSquareTextIcon,
  RefreshCwIcon,
  ScrollTextIcon,
  ShieldCheckIcon,
  SmartphoneIcon
} from 'lucide-react';

import { useListEvents } from '@cdf/api-client-react';
import { useAuth } from '@/components/auth/auth-provider';
import { defaultPathForRole, useSession } from '@/components/auth/user-provider';
import { ProfileMenu } from '@/components/layout/profile-menu';
import { ThemeToggle } from '@/components/theme/theme-toggle';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const APP_NAME = 'School Attendance';
const RETURN_TO_KEY = 'auth:returnTo';

const features = [
  {
    title: 'SMS preference capture',
    description:
      'Parents reply by text to confirm how their child is leaving — by themselves, with a guardian, or back to school.',
    icon: MessageSquareTextIcon
  },
  {
    title: 'Live sign-out register',
    description:
      'Teachers run roll call in real time. The register updates as students sign out and preferences come in.',
    icon: ClipboardListIcon
  },
  {
    title: 'Volunteer tracking',
    description:
      'Keep a clear list of helpers on site for each event so staff always know who is supporting the day.',
    icon: HeartHandshakeIcon
  },
  {
    title: 'Full audit trail',
    description:
      'Every sign-out is recorded with time, preference, and context — ready when you need accountability.',
    icon: ScrollTextIcon
  }
] as const;

const steps = [
  {
    step: '1',
    title: 'Set up the event',
    description: 'Create an event, add students, and open the live register for the day.'
  },
  {
    step: '2',
    title: 'Capture parent replies',
    description: 'SMS preferences flow in automatically so staff see the latest intent at a glance.'
  },
  {
    step: '3',
    title: 'Sign students out safely',
    description: 'Mark departures on the register and keep a complete audit trail for the event.'
  }
] as const;

function BrandMark() {
  return (
    <div className="flex items-center gap-3">
      <span className="bg-primary text-primary-foreground ring-primary/20 flex size-10 items-center justify-center rounded-xl shadow-sm ring-4">
        <GraduationCapIcon className="size-5" aria-hidden />
      </span>
      <span className="flex flex-col leading-none">
        <span className="text-sm font-bold tracking-tight">{APP_NAME}</span>
        <span className="text-muted-foreground mt-1 text-[10px] font-semibold tracking-[0.16em] uppercase">
          Operations Console
        </span>
      </span>
    </div>
  );
}

export function HomePage() {
  const navigate = useNavigate();
  const { session, signIn } = useSession();
  const { popupBlocked } = useAuth();
  const signedIn = session.status === 'signedIn';
  const loading = session.status === 'loading';
  const { data: eventData } = useListEvents();
  const activeEvent = eventData?.events?.find((e) => e.status === 'active');
  const mobileRegisterPath = activeEvent
    ? `/events/${activeEvent.id}/register`
    : '/events';

  React.useEffect(() => {
    document.title = `${APP_NAME} · Safer event sign-out`;
    return () => {
      document.title = APP_NAME;
    };
  }, []);

  const appHome =
    session.status === 'signedIn'
      ? defaultPathForRole(session.user.role, session.user.isActive)
      : '/dashboard';

  // After Entra redirect, send signed-in users into the app (return path or stay for Open app CTA).
  React.useEffect(() => {
    if (!signedIn) return;
    const returnTo = sessionStorage.getItem(RETURN_TO_KEY);
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      sessionStorage.removeItem(RETURN_TO_KEY);
      navigate(returnTo, { replace: true });
    }
  }, [signedIn, navigate]);

  const handleSignIn = () => {
    sessionStorage.setItem(RETURN_TO_KEY, appHome);
    signIn();
  };

  return (
    <div className="bg-background text-foreground min-h-svh">
      <a
        href="#main-content"
        className="bg-card text-foreground ring-ring sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-lg focus:px-3 focus:py-2 focus:text-sm focus:ring-2 focus:shadow-lg"
      >
        Skip to main content
      </a>

      <header className="border-border/80 bg-background/90 supports-[backdrop-filter]:bg-background/75 sticky top-0 z-40 border-b backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 md:px-6">
          <BrandMark />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {loading ? null : signedIn ? (
              <>
                <Button variant="outline" asChild>
                  <Link to={mobileRegisterPath}>
                    <SmartphoneIcon /> <span className="hidden sm:inline">Mobile register</span>
                  </Link>
                </Button>
                <ProfileMenu />
              </>
            ) : (
              <Button onClick={handleSignIn}>
                <LogInIcon /> Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="outline-none">
        {/* Hero */}
        <section className="border-border/60 relative overflow-hidden border-b">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,oklch(0.5_0_0_/_0.08),transparent_55%)] dark:bg-[radial-gradient(ellipse_at_top,oklch(0.85_0_0_/_0.08),transparent_55%)]"
          />
          <div className="relative mx-auto grid max-w-6xl gap-10 px-4 py-16 md:grid-cols-[minmax(0,1.2fr)_minmax(0,0.9fr)] md:items-center md:px-6 md:py-24">
            <div className="flex flex-col gap-6">
              <Badge variant="outline" className="w-fit gap-1.5">
                <ShieldCheckIcon className="size-3.5" aria-hidden />
                Built for school events
              </Badge>
              <div className="flex flex-col gap-4">
                <h1 className="text-4xl font-bold tracking-tight text-balance sm:text-5xl">
                  Safer student sign-out for every school event
                </h1>
                <p className="text-muted-foreground max-w-xl text-lg text-pretty">
                  Capture parent SMS preferences, run a live departure register, track volunteers, and
                  keep a complete audit trail — all in one calm, focused console for staff.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {signedIn ? (
                  <Button size="lg" asChild>
                    <Link to={appHome}>
                      Open app <ArrowRightIcon />
                    </Link>
                  </Button>
                ) : (
                  <Button size="lg" onClick={handleSignIn} disabled={loading}>
                    <LogInIcon /> Sign in with Microsoft
                  </Button>
                )}
                <Button size="lg" variant="outline" asChild>
                  <a href="#how-it-works">See how it works</a>
                </Button>
                {signedIn && (
                  <Button size="lg" variant="secondary" asChild>
                    <Link to={mobileRegisterPath}>
                      <SmartphoneIcon /> Mobile register
                    </Link>
                  </Button>
                )}
              </div>
              {!signedIn && popupBlocked && (
                <div
                  role="alert"
                  className="border-border bg-muted/50 flex flex-col gap-3 rounded-lg border p-4 text-sm"
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
                    <span>
                      Sign-in popup was blocked by your browser. Open this page in a new tab to sign in, or retry below.
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" asChild>
                      <a href={window.location.href} target="_blank" rel="noopener noreferrer">
                        <ExternalLinkIcon /> Open in new tab
                      </a>
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleSignIn}>
                      <RefreshCwIcon /> Retry
                    </Button>
                  </div>
                </div>
              )}
              <ul className="text-muted-foreground flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:gap-x-5">
                {['Entra ID sign-in', 'Live register updates', 'SMS-ready workflow'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle2Icon className="text-foreground size-4 shrink-0" aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <Card className="border-primary/10 shadow-xl shadow-black/5">
              <CardHeader>
                <CardTitle className="text-base">What staff get on day one</CardTitle>
                <CardDescription>
                  A single place to run events without juggling spreadsheets and group chats.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3">
                {[
                  'Event workspace with live attendance',
                  'Parent preference status at a glance',
                  'Volunteer list per event',
                  'Searchable sign-out history'
                ].map((item) => (
                  <div
                    key={item}
                    className="bg-muted/50 flex items-start gap-3 rounded-xl border px-3 py-3 text-sm"
                  >
                    <CheckCircle2Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
                    <span>{item}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Features */}
        <section className="mx-auto max-w-6xl px-4 py-16 md:px-6 md:py-20" aria-labelledby="features-heading">
          <div className="mb-10 max-w-2xl">
            <h2 id="features-heading" className="text-3xl font-bold tracking-tight">
              Capability highlights
            </h2>
            <p className="text-muted-foreground mt-3 text-base text-pretty">
              Everything your team needs to run event departure with clarity — from the first parent
              reply to the final audit entry.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {features.map((feature) => (
              <Card key={feature.title} className="h-full">
                <CardHeader className="gap-3">
                  <span className="bg-secondary text-foreground flex size-10 items-center justify-center rounded-xl">
                    <feature.icon className="size-5" aria-hidden />
                  </span>
                  <div className="space-y-1.5">
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                    <CardDescription className="text-sm leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section
          id="how-it-works"
          className="border-border/60 bg-muted/35 border-y"
          aria-labelledby="how-heading"
        >
          <div className="mx-auto max-w-6xl px-4 py-16 md:px-6 md:py-20">
            <div className="mb-10 max-w-2xl">
              <h2 id="how-heading" className="text-3xl font-bold tracking-tight">
                How it works
              </h2>
              <p className="text-muted-foreground mt-3 text-base text-pretty">
                Three simple steps from planning to a complete, defensible record of the day.
              </p>
            </div>
            <ol className="grid gap-4 md:grid-cols-3">
              {steps.map((item) => (
                <li key={item.step}>
                  <Card className="h-full">
                    <CardHeader>
                      <Badge variant="secondary" className="mb-2 w-fit tabular-nums">
                        Step {item.step}
                      </Badge>
                      <CardTitle className="text-lg">{item.title}</CardTitle>
                      <CardDescription className="text-sm leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-6xl px-4 py-16 md:px-6 md:py-20" aria-labelledby="cta-heading">
          <Card className="border-primary/15 overflow-hidden">
            <CardContent className="flex flex-col items-start gap-6 p-8 md:flex-row md:items-center md:justify-between md:p-10">
              <div className="max-w-xl space-y-2">
                <h2 id="cta-heading" className="text-2xl font-bold tracking-tight md:text-3xl">
                  Ready to run your next event with confidence?
                </h2>
                <p className="text-muted-foreground text-pretty">
                  Sign in with your school Microsoft account to open the operations console.
                </p>
              </div>
              {signedIn ? (
                <Button size="lg" asChild>
                  <Link to={appHome}>
                    Open app <ArrowRightIcon />
                  </Link>
                </Button>
              ) : (
                <Button size="lg" onClick={handleSignIn} disabled={loading}>
                  <LogInIcon /> Sign in
                </Button>
              )}
            </CardContent>
          </Card>
        </section>
      </main>

      <footer className="border-border/80 text-muted-foreground border-t py-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 text-sm md:flex-row md:items-center md:justify-between md:px-6">
          <p>{APP_NAME}</p>
          <p>Secure staff access via Microsoft Entra ID.</p>
        </div>
      </footer>
    </div>
  );
}
